import CustomHtmlKeyboardResponsePlugin from "../../customPlugin/customHtmlKeyboardResponse";

import { KEY_CODES } from "../../gearshiftGame/constants";
import { Game } from "../../gearshiftGame/game";
import { Score } from "../../gearshiftGame/score";
import { createStimulus, hideElement } from "../../gearshiftGame/utils";
import {
  getGearshiftMissionConfig,
} from "../../lib/utils";
import { buildAchievementsTrial } from "../trials/achievements";
import { buildFeedbackTrial } from "../trials/feedback";
import { buildInstructionsTrial } from "../trials/instructions";
import { tutorialToggles } from "../../config/tutorialToggles";
import { updateDebugMissionConfig } from "../ui/debugHelpers";
import { buildBasicControlsTutorial } from "./utils/buildBasicControlsTutorial";
import { buildPracticeChoiceTrial, createPracticeChoiceChecker } from "./utils/repracticeUtils";
import { addFirstTaskMetadata } from "./utils/missionHelpers";
import { buildAdaptiveTrialsNode } from "./utils/adaptiveMissionTrials";
import {
  buildPhase0,
  buildPhase1,
  buildPhase2,
  buildPhase3,
  buildPhase4,
  buildPhase5,
  buildPhase7,
} from "./phases/missionThreePhases";
import { buildTutorialWithPhasesMissionThree } from "./utils/buildTutorialWithPhasesMissionThree";
import { buildSkipMissionProcedure } from "./skipMission";
import {
  MISSION_THREE_PAGE_COUNT,
  buildMissionThreeInstructionPages,
} from "./instructionBuilders/missionThree/buildMissionThreeInstructionPages";
import {
  injectHQMessage,
  removeHQMessage,
} from "./hq/buildHQMessage.js";
import { injectSurveyPanel, removeSurveyPanel } from "./hq/buildHQSurveyPanel.js";
import { getHQMessagePanelConfig } from "./hq/localHQMessages.js";

/**
 * @type {number}
 * @description The current mission
 */
const MISSION_NUMBER = 3;

/**
 * @type {Score}
 * @description The user's score
 * NOTE: Used here so we don't have to pass it through every single function
 */
let SCORE;

/**
 * Global counter for tutorial failures in this mission
 */
let tutorialFailureCount = 0;

/**
 * Global failure limit for this mission
 */
let failureLimit = 2;

/**
 * Builds the first mission of the game
 * @param {JsPsych} jsPsych jsPsych instance being used to run the task
 * @param {Object} metadata The global metadata of the experiment
 * @param {string} participantID The ID of the participant running the mission
 * @returns The timeline object for the first mission
 */
export async function buildMissionThree(jsPsych, metadata, participantID) {
  // Reset tutorial failure counter when entering this mission
  tutorialFailureCount = 0;

  // Check if a config file was given for this mission
  let config;
  try {
    config = await getGearshiftMissionConfig(jsPsych, metadata, MISSION_NUMBER);
  } catch (e) {
    console.error(`Unable to load mission config file for mission ${MISSION_NUMBER}`, e);
  }

  // No config file was given for mission 1, skip this mission (no file given or failed to load the file)
  if (!config) return buildSkipMissionProcedure(MISSION_NUMBER);

  // Config file given - build the mission!
  console.log("Mission Three Configuration:", config);

  // Update debug overlay with mission configuration
  updateDebugMissionConfig(jsPsych, config);

  // Initialize variables shared across the mission
  const {
    missionLives,
    missionMinScore,
    missionMaxCrash,
    missionNumTrials,
    instructionsMaxRetries,
    instructionsAudio,
    trials,
    thisFile,
  } = config;
  // Get the tutorial settings for this mission
  const missionTutorials = {
    ...(tutorialToggles.mission3 ?? {}),
    ...(config.tutorials ?? {}),
    ...(instructionsAudio !== undefined ? { instructionsAudio } : {}),
  };
  // Get the failure limit from the mission config, fallback to tutorial toggles
  failureLimit = instructionsMaxRetries ?? missionTutorials.failsCountLimit ?? 2;
  SCORE = new Score(
    jsPsych,
    missionLives, // # of lives to start the mission with
    metadata.rewardReductionByReset // Amount to reduce score by when the user runs out of lives
  );

  // Adaptive missions: once static trials run out, keep fetching more until
  // blockType 98 signals mission end. See adaptiveMissionTrials.js.
  const lastConfiguredTrial = trials[trials.length - 1];
  let adaptiveTrials = null;
  if (config.missionIsAdaptive) {
    const templateTrial =
      [...trials].reverse().find((trial) => trial.trialType === "TASK") ?? lastConfiguredTrial;

    adaptiveTrials = buildAdaptiveTrialsNode({
      jsPsych,
      metadata,
      participantID,
      missionNumber: MISSION_NUMBER,
      initialCursor: {
        block: lastConfiguredTrial?.block ?? 0,
        trialNumber: lastConfiguredTrial?.trialNumber ?? 0,
      },
      templateTrial,
      score: SCORE,
      trialBuilders: {
        TASK: buildMissionThreeTask,
        MESSAGE: buildMissionThreeMessage,
        SURVEY: buildMissionThreeSurvey,
        FEEDBACK: (jsPsych, metadata, trialConfig) =>
          buildMissionThreeFeedback(jsPsych, trialConfig, missionMinScore, missionMaxCrash),
      },
      createStimulusFn: (trialConfig) => createStimulus(MISSION_NUMBER, missionNumTrials, trialConfig),
    });
  }

  // Build the timeline from the config's trials array
  const missionThreeTimeline = trials.map((trialConfig, index) => {
    const stimulus = () => createStimulus(MISSION_NUMBER, missionNumTrials, trialConfig);
    // Only the last static trial needs to warm the adaptive queue.
    const prefetchNextBatch =
      index === trials.length - 1 ? adaptiveTrials?.prefetchNextBatch : undefined;
    switch (trialConfig.trialType) {
      case "INSTRUCTION":
        // NOTE: Instructions trial is the first trial of the mission
        // return SKIP_TRIAL; // TEMP: Skip the trial for testing purposes
        return buildMissionThreeInstructions(jsPsych, trialConfig, metadata, stimulus(), trials, missionTutorials);
      case "TASK":
        // return SKIP_TRIAL; // TEMP: Skip the trial for testing purposes
        return buildMissionThreeTask(jsPsych, metadata, trialConfig, stimulus, prefetchNextBatch);
      case "MESSAGE":
        // return SKIP_TRIAL; // TEMP: Skip the trial for testing purposes
        return buildMissionThreeMessage(jsPsych, metadata, trialConfig, stimulus);
      case "FEEDBACK":
        // NOTE: Feed back trial is the last trial of the mission
        // return SKIP_TRIAL; // TEMP: Skip the trial for testing purposes
        return buildMissionThreeFeedback(jsPsych, trialConfig, missionMinScore, missionMaxCrash);
      case "SURVEY":
        return buildMissionThreeSurvey(jsPsych, metadata, trialConfig, stimulus);
      default:
        throw new Error("An unknown trial type was found:", trialConfig.trialType);
    }
  });

  // Add task_metadata to the first TASK trial
  addFirstTaskMetadata(missionThreeTimeline, trials, metadata);

  if (adaptiveTrials) missionThreeTimeline.push(adaptiveTrials.node);

  return {
    data: {
      missionNumber: MISSION_NUMBER,
      missionFile: thisFile,
      missionNumTrials,
      missionIsAdaptive: !!config.missionIsAdaptive,
      skipped: false,
    },
    save_trial_parameters: { stimulus: false },
    timeline: missionThreeTimeline,
  };
}

/**
 * Builds the trial for displaying the instructions for mission three
 * @param {JsPsych} jsPsych jsPsych instance being used to run the task
 * @param {Object} trialConfig The configuration object for a specific trial
 * @param {Object} metadata The global metadata of the experiment
 * @param {String} stimulus The HTML stimulus to show
 * @param {Array} allTrials All trials from the mission config
 * @param {Object} missionTutorials Tutorial toggles for this mission
 * @returns
 */
function buildMissionThreeInstructions(jsPsych, trialConfig, metadata, stimulus, allTrials, missionTutorials = {}) {
  // Get audio enabled status from backend config, fallback to local configs
  const instructionAudioEnabled = missionTutorials.instructionsAudio ?? tutorialToggles.mission3?.instructionsAudio ?? true;
  const instructionAudio = (src) => (instructionAudioEnabled ? src : undefined);

  const tutorialConfig = { ...(allTrials?.find((t) => t.trialType === "TASK") || trialConfig) };
  tutorialConfig.trialNumber = 1;
  const tutorialStimulus = createStimulus(MISSION_NUMBER, 1, tutorialConfig);

  const pages = buildMissionThreeInstructionPages(jsPsych, instructionAudio);
  const totalPages = MISSION_THREE_PAGE_COUNT;

  const timeline = [];
  const tutorialEnabled = (key, fallback = true) => missionTutorials[key] ?? fallback;

  // Check if we should ask user about practice
  const askRepractice = missionTutorials.askRepractice ?? true;

  // Helper function to check if user wants practice
  const checkPracticeChoice = createPracticeChoiceChecker(jsPsych, askRepractice);

  // Page 1
  const firstPages = pages.slice(0, Math.min(1, pages.length));
  if (firstPages.length) {
    timeline.push(
      buildInstructionsTrial(jsPsych, firstPages, {
        total_pages: totalPages,
        page_offset: 0,
      })
    );
  }

  // Add practice choice screen after page 1
  if (pages.length >= 1 && askRepractice) {
    timeline.push(buildPracticeChoiceTrial(jsPsych));
  }

  // Basic Driving Controls tutorials after page 1
  if (pages.length >= 1 && tutorialEnabled("decel")) {
    timeline.push({
      timeline: [
        buildBasicControlsTutorial(
          jsPsych,
          metadata,
          tutorialConfig,
          tutorialStimulus,
          "decel",
          buildMissionThreeTaskWithPhasesCustom
        ),
      ],
      conditional_function: checkPracticeChoice,
    });
  }
  if (pages.length >= 1 && tutorialEnabled("accel")) {
    timeline.push({
      timeline: [
        buildBasicControlsTutorial(
          jsPsych,
          metadata,
          tutorialConfig,
          tutorialStimulus,
          "accel",
          buildMissionThreeTaskWithPhasesCustom
        ),
      ],
      conditional_function: checkPracticeChoice,
    });
  }
  if (pages.length >= 1 && tutorialEnabled("reveal")) {
    timeline.push({
      timeline: [
        buildBasicControlsTutorial(
          jsPsych,
          metadata,
          tutorialConfig,
          tutorialStimulus,
          "reveal",
          buildMissionThreeTaskWithPhasesCustom
        ),
      ],
      conditional_function: checkPracticeChoice,
    });
  }

  // Remaining pages (2-8)
  if (pages.length > 1) {
    const remaining = pages.slice(1);
    timeline.push(
      buildInstructionsTrial(jsPsych, remaining, {
        total_pages: totalPages,
        page_offset: 1,
      })
    );
  }

  return {
    data: { trialConfig },
    timeline,
    on_finish: (data) => SCORE.assignScore(data),
  };
}

/**
 * Builds a tutorial with selected phases for Mission 3 (customizable options)
 * @param {JsPsych} jsPsych jsPsych instance being used to run the task
 * @param {Object} metadata The global metadata of the experiment
 * @param {Object} trialConfig The task configuration used to build the trial
 * @param {String} stimulus The HTML stimulus to show
 * @param {Array} phases Array of phase numbers to include in the tutorial
 * @param {Object} options Optional overrides: agentFile, disableAcceleration, disableDeceleration, hideTargetCar, customTrialConfigOverrides, ready/congrats overlays
 * @returns A jsPsych node with the tutorial phases
 */
function buildMissionThreeTaskWithPhasesCustom(
  jsPsych,
  metadata,
  trialConfig,
  stimulus,
  phases = [0, 1, 2, 3, 4, 5],
  options = {}
) {
  const buildersMap = {
    0: buildPhase0,
    1: buildPhase1,
    2: buildPhase2,
    3: buildPhase3,
    4: buildPhase4,
    5: buildPhase5,
    7: buildPhase7,
  };

  const { agentFile, tutorialHeader, ...customOptions } = options;

  // Recreate stimulus with tutorialHeader if provided
  let finalStimulus = stimulus;
  if (tutorialHeader) {
    const configForStimulus = {
      ...trialConfig,
      ...(agentFile ? { agentFile } : {}),
      tutorialHeader,
    };
    finalStimulus = createStimulus(MISSION_NUMBER, 1, configForStimulus);
  }
  const defaultOverlayOptions = {
    ready: {
      forwardText: `[press "__FORWARD_KEY__" twice to start]`,
      backText: `[press "__REPLAY_KEY__" to play video]`,
      imageOrVideoUrl: "assets/images/instructions/video_placeholder.svg",
      showText: false,
    },
    congrats: {
      forwardText: `[press "__FORWARD_KEY__" to continue ->]`,
      backText: "",
      imageOrVideoUrl: "assets/images/instructions/demo_congratulations.png",
      forwardTextCrash: `[press "__FORWARD_KEY__" to try again ]`,
      backTextCrash: "",
      imageOrVideoUrlCrash: "assets/images/instructions/demo_try_again.png",
      showText: false,
    },
  };

  const overlayOptions = {
    ready: { ...defaultOverlayOptions.ready, ...customOptions.ready },
    congrats: { ...defaultOverlayOptions.congrats, ...customOptions.congrats },
  };

  const trialConfigWithOverrides = {
    ...trialConfig,
    ...(agentFile ? { agentFile } : {}),
    ...(customOptions.disableAcceleration !== undefined
      ? { disableAcceleration: customOptions.disableAcceleration }
      : {}),
    ...(customOptions.disableDeceleration !== undefined
      ? { disableDeceleration: customOptions.disableDeceleration }
      : {}),
    ...(customOptions.hideTargetCar !== undefined
      ? { hideTargetCar: customOptions.hideTargetCar }
      : {}),
    ...(customOptions.customTrialConfigOverrides || {}),
  };

  return buildTutorialWithPhasesMissionThree({
    jsPsych,
    metadata,
    score: SCORE,
    trialConfig: trialConfigWithOverrides,
    stimulus: finalStimulus,
    phases,
    buildersMap,
    overlayOptions,
    hooks: {
      onBeforeCongratsOverlay: ({ tryAgain }) => {
        if (!tryAgain) return false;

        tutorialFailureCount += 1;
        console.log(
          `❌ Tutorial failed (attempt ${tutorialFailureCount}/${failureLimit})`
        );

        if (tutorialFailureCount >= failureLimit) {
          window.location.href = "supportPage.html";
          return true;
        }

        return false;
      },
    },
  });
}

/**
 * Builds the trial needed for mission one's task
 * @param {JsPsych} jsPsych jsPsych instance being used to run the task
 * @param {Object} metadata The global metadata of the experiment
 * @param {Object|Function} trialConfig Trial config, or a getter for it (adaptive loop
 *   uses a getter so the Game below is rebuilt fresh per queued trial - see adaptiveMissionTrials.js)
 * @param {String} stimulus The HTML stimulus to show
 * @param {() => Promise<void>} [prefetchNextBatch] Warms the next adaptive batch from phase 7
 * @param {Score} score The user's current score
 * @returns
 */
function buildMissionThreeTask(jsPsych, metadata, trialConfig, stimulus, prefetchNextBatch) {
  const resolveTrialConfig = typeof trialConfig === "function" ? trialConfig : () => trialConfig;
  // Built lazily so a getter-based trialConfig is read only once the trial actually starts.
  let newGame;
  return {
    type: CustomHtmlKeyboardResponsePlugin,
    stimulus,
    data: { trialConfig: resolveTrialConfig },
    timeline: [
      //Phase 0: Board initializes, end of phase 0 is when target car appears
      {
        choices: "NO_KEYS",
        trial_duration: () => {
          newGame = new Game(jsPsych, metadata, SCORE, resolveTrialConfig());
          // Exposed globally so aiAgentStateReader (see src/experiment/procedures/aiAgent)
          // can read live game state
          window._gear_currentGame = newGame;
          return newGame.targetLaunchDelay;
        },
        on_load: () => {
          newGame.startPhase0();
        },
        on_finish: (data) => {
          data.name = "initializeTarget";
          data.driverSpeed = newGame.driverCarSpeed;
          data.distanceToTarget = newGame.driverDistanceToTarget;
          data.trialStart = {
            name: "trialStart",
            driverSpeed: newGame.driverCarSpeed,
            timestamp: jsPsych.data.get().last(2).values()[0].time_elapsed,
          };
          SCORE.assignScore(data);
        },
      },

      // Phase 1: Target car begins traveling towards driver car.
      // Terminates when target car/driver car lock in proximity zone
      {
        choices: "NO_KEYS", // NOTE: Keyboard events are handled inside Game
        on_load: () => newGame.startPhase1(),
        on_finish: (data) => {
          data.name = "engageTarget";
          data.pulseTTLtrialStart = newGame.ttlPulseFired ? 1 : 0;
          data.TTLpulse_time = newGame.ttlPulseTime;
          data.cueCarOn = {
            name: "cueCarOn",
            driverSpeed: newGame.driverCarSpeed,
            timestamp: jsPsych.data.get().last(1).values()[0].time_elapsed,
            distanceToTarget: newGame.driverDistanceToTarget,
            csi: newGame.csi,
          };

          // Crash - move on to the next timeline (e.g. skip the rest of this Game's phases)
          if (data.crashed) jsPsych.endCurrentTimeline();
          else data.crashed = false;

          SCORE.assignScore(data);
        },
      },

      // Phase 2: Start of CSI period, triggered by proximity zone initiation in previous phase
      // Terminates when CSI is over and it's time to show the license
      {
        choices: "NO_KEYS",
        trial_duration: () => newGame.csi,
        on_load: () => newGame.startPhase2(),
        on_finish: (data) => {
          data.name = "licensePlateOn";
          data.timestamp = jsPsych.data.get().last(2).values()[0].time_elapsed;
          data.driverSpeed = newGame.driverCarSpeed;
          data.distanceToTarget = jsPsych.data.get().last(2).values()[0].distanceToTarget;
          SCORE.assignScore(data);
        },
      },

      // Phase 3: At the start of this phase, the license is displayed
      // Package 2 will appear in the horizon and begin travelling down the highway
      // if occurenceFrontbus == 1, then front bus will also begin travelling down the highway
      // At this point, the user can make a lane choice.
      // Package 2 will always be in lane 1, the user can only choose lane 1 if package 2 is still on the highway
      // if it already exited the highway, you cannot choose it.
      {
        choices: "NO_KEYS", // NOTE: Keyboard events are handled inside Game
        on_load: () => newGame.startPhase3_M3(),
        on_finish: (data) => {
          data.name = "responseKeyPressed";
          data.response_keypress_time = data.time_elapsed;
          data.keyCode = KEY_CODES[data.response_event_code ?? data.response] ?? null; // keyCode is null if no response is given
          data.responseTime =
            data.time_elapsed - jsPsych.data.get().last(2).values()[0].time_elapsed;
          data.driverSpeed = newGame.driverCarSpeed;
          // displayLicense()/captureLicensePlateOnTime() only run once startPhase3_M3's
          // on_load fires, which is after the "licensePlateOn" (phase 2) row already saved -
          // so this is the earliest row where the value is actually available.
          data.license_plate_on_time = newGame.licensePlateOnTime;
          // Captures whatever's been set so far - null unless the frontbus/escape-package
          // timers already fired by this point. Phase 7 overwrites these with the final
          // value on the normal path; a crash here ends the timeline before phase 7 runs,
          // so this is the only row that gets saved in that case.
          data.frontbus_on_time = newGame.frontbusOnTime;
          data.escape_package_on_time = newGame.escapePackageOnTime;
          // Ghost twin's fall duration is response-independent, so it's rarely settled by this
          // early row - phase 7 below overwrites it with the final value on the normal path.
          data.escape_package_ghost_on_time = newGame.escapePackageGhostOnTime;
          data.escape_package_ghost_off_time = newGame.escapePackageGhostOffTime;

          // Crash - move on to the next timeline (e.g. skip the rest of this Game's phases)
          // TODO: need this?
          if (data.crashed) jsPsych.endCurrentTimeline();
          else data.crashed = false;

          SCORE.assignScore(data);
        },
      },

      // Phase 4: Once lane has been chosen in phase 3, phase 4 begins with the driver car moving to the user chosen lane
      // Terminates when the driver car reaches the chosen lane.
      {
        choices: "NO_KEYS",
        // Start the phase based on the lane chosen
        on_load: () => newGame.startPhase4(jsPsych.data.get().last(1).values()[0].keyCode),
        on_finish: (data) => {
          data.name = "driverInResponseLane";
          data.driverSpeed = newGame.driverCarSpeed;
          SCORE.assignScore(data);
        },
      },

      //Phase 5
      // Once driver is in chosen lane, the package will appear and travel down the correct lane. Simultaneously, the target car also moves down the highway until it disappears.
      // Once the package reaches the end of the highway, a score will show above the driver car, showing the points the driver car received in this game trial.
      {
        choices: "NO_KEYS",
        on_load: () => newGame.startPhase5(jsPsych.data.get().last(2).values()[0].keyCode),
        on_finish: (data) => {
          data.name = "packageDisappears";
          data.driverSpeed = newGame.driverCarSpeed;
          data.emotion = newGame.emotion;
          data.packageInTargetLane = {
            timestamp: jsPsych.data.get().last(2).values()[0].time_elapsed,
            driverSpeed: newGame.driverCarSpeed,
            packageDistance: newGame.packageDistanceToTarget,
          };
          data.targetDisappears = {
            timestamp: newGame.targetCarDisappearTimestamp,
            driverSpeed: newGame.driverCarSpeed,
          };
          data.emotionReaction = {
            emotion: newGame.emotion,
            driverSpeed: newGame.driverCarSpeed,
            timestamp:
              jsPsych.data.get().last(1).values()[0].time_elapsed - newGame.feedbackDuration,
          };

          SCORE.assignScore(data);
        },
      },

      // Phase 7: Driver car travels back to the center of the highway
      // Triggered once user completes survey
      {
        choices: "NO_KEYS",
        on_load: () => {
          newGame.startPhase7();
          prefetchNextBatch?.();
        },
        on_finish: (data) => {
          data.name = "driverAtCenter";
          data.driverToCenter = {
            timestamp: jsPsych.data.get().last(2).values()[0].time_elapsed,
            driverSpeed: newGame.driverCarSpeed,
          };
          data.driverSpeed = newGame.driverCarSpeed;
          data.frontbus_on_time = newGame.frontbusOnTime;
          data.escape_package_on_time = newGame.escapePackageOnTime;
          data.escape_package_ghost_on_time = newGame.escapePackageGhostOnTime;
          data.escape_package_ghost_off_time = newGame.escapePackageGhostOffTime;
          SCORE.assignScore(data);
        },
      },
    ],
  };
}

/**
 * Builds the trial needed to display the headquarter message
 * @param {JsPsych} jsPsych jsPsych instance being used to run the task
 * @param {Object} metadata The global metadata of the experiment
 * @param {Object|Function} trialConfig Trial config, or a getter for it (see `buildMissionThreeTask`)
 * @param {String} stimulus The HTML stimulus to show
 * @param {Score} score The user's current score
 * @returns
 */
function buildMissionThreeMessage(jsPsych, metadata, trialConfig, stimulus) {
  const resolveTrialConfig = typeof trialConfig === "function" ? trialConfig : () => trialConfig;
  let newGame;
  return {
    type: CustomHtmlKeyboardResponsePlugin,
    stimulus,
    data: { trialConfig: resolveTrialConfig },
    choices: ["Space"],
    on_load: () => {
      const resolvedTrialConfig = resolveTrialConfig();
      newGame = new Game(jsPsych, metadata, SCORE, resolvedTrialConfig);
      // Exposed globally so aiAgentStateReader (see src/experiment/procedures/aiAgent)
      // can read live game state
      window._gear_currentGame = newGame;
      const hqConfig = getHQMessagePanelConfig(MISSION_NUMBER, resolvedTrialConfig.agentFile);
      newGame.startPhase6();
      if (hqConfig) injectHQMessage(jsPsych, hqConfig);
    },
    on_finish: (data) => {
      removeHQMessage();
      SCORE.assignScore(data);
    },
  };
}

/**
 * Builds the trial needed for mission one's feedback
 * @param {JsPsych} jsPsych jsPsych instance being used to run the task
 * @param {Object|Function} trialConfig Trial config, or a getter for it (see `buildMissionThreeTask`)
 * @param {String} missionMinScore Per mission minimum score to continue
 * @param {String} missionMaxCrash Per mission max crashes allowed
 * @returns
 */
function buildMissionThreeFeedback(jsPsych, trialConfig, missionMinScore, missionMaxCrash) {
  const resolveTrialConfig = typeof trialConfig === "function" ? trialConfig : () => trialConfig;
  return {
    data: { trialConfig: resolveTrialConfig },
    timeline: [
      // Display the total rewards
      ...buildFeedbackTrial(
        jsPsych,
        SCORE,
        missionMinScore,
        missionMaxCrash,
        "assets/images/postgame/FeedbackScreen_Mission3.svg"
      ),
      // Display the user's achievements
      buildAchievementsTrial("assets/images/postgame/achievementScreen_mission3.svg"),
    ],
  };
}

/**
 * Builds the trial needed for mission three's survey
 * @param {JsPsych} jsPsych jsPsych instance being used to run the task
 * @param {Object} metadata The global metadata of the experiment
 * @param {Object|Function} trialConfig Trial config, or a getter for it (see `buildMissionThreeTask`)
 * @param {String} stimulus The HTML stimulus to show
 * @returns
 */
function buildMissionThreeSurvey(jsPsych, metadata, trialConfig, stimulus) {
  const resolveTrialConfig = typeof trialConfig === "function" ? trialConfig : () => trialConfig;
  const surveyPortrait = "hq-happy";
  let newGame;

  return {
    type: CustomHtmlKeyboardResponsePlugin,
    stimulus,
    data: { trialConfig: resolveTrialConfig },
    timeline: [
      //Phase 0: Board initializes, end of phase 0 is when target car appears
      {
        choices: "NO_KEYS",
        trial_duration: () => {
          newGame = new Game(jsPsych, metadata, SCORE, resolveTrialConfig());
          // Exposed globally so aiAgentStateReader (see src/experiment/procedures/aiAgent)
          // can read live game state
          window._gear_currentGame = newGame;
          return newGame.targetLaunchDelay;
        },
        on_load: () => newGame.startPhase0(),
      },
      // Phase 6: Headquarter Survey
      {
        on_load: () => {
          newGame.startPhase(6);
          hideElement(newGame.targetCar);
          hideElement(newGame.agentImage);
          newGame.playEffect("message");
          injectSurveyPanel(jsPsych, {
            portrait: surveyPortrait,
            promptText: "How are you?"
          });
        },
        choices: ["KeyS", "KeyD", "KeyF", "KeyK", "KeyL", "Semicolon"],
        trial_duration: () => newGame.headquarterMessageDuration,
        on_finish: (data) => {
          removeSurveyPanel();
          data.name = "messageFromHQ";
        },
      },
    ],
  };
}
